import { Icon, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, makeStyles, Tooltip } from '@material-ui/core';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LanguageString } from '..';
import { ChildRoutes, useChildLinks } from '../../contexts';
import { Assignment, Milestone, RoadmapMilestone, Task, TaskActionType } from '../../dto';
import { MilestoneState } from '../../helpers/userHelper';
import { Goal } from './Goal';

type UserTask = {
  task: Task;
  assignment: Assignment;
};

export type UserMilestone = {
  milestone: Milestone;
  rmMilestone: RoadmapMilestone;
  tasks: UserTask[];
  // buttons?: (rmMsId: number | undefined) => React.ReactNode;
};

type MilestoneListProps = {
  userMilestones: UserMilestone[];
  taskDetail?: boolean;
  buttons: (rmMs: RoadmapMilestone) => React.ReactNode;
};

const useStyles = makeStyles(theme => ({
  container: {
    '& > .MuiListItem-container': {
      display: 'flex',
      '& > .MuiListItem-secondaryAction': {
        paddingRight: 'unset'
      },
      '& > .MuiListItemSecondaryAction-root': {
        position: 'unset',
        display: 'flex',
        transform: 'translateY(0)',
        marginRight: '16px'
      }
    }
  }
}));

const taskStatus = (assignment: Assignment): MilestoneState => assignment?.isComplete ? MilestoneState.COMPLETE : assignment?.startedAt ? MilestoneState.STARTED : MilestoneState.NOT_STARTED;

const milestoneStatus = (userMilestone: UserMilestone): MilestoneState => {
  const assignments = userMilestone.tasks.map(t => t.assignment);
  const isComplete = assignments && assignments.length > 0 ? assignments.every(a => a.isComplete) : false;
  const isStarted = assignments ? assignments.some(a => a.startedAt != null) : false;

  return isComplete ? MilestoneState.COMPLETE : isStarted ? MilestoneState.STARTED : MilestoneState.NOT_STARTED;
};

const MsIcon: React.FC<{ status: MilestoneState }> = ({ status }) =>
  status === MilestoneState.COMPLETE ? (
    <Tooltip title={<LanguageString groupName="ROADMAP" resourceName="MILESTONE_COMPLETED" defaultText="Completed" />}>
      <Icon style={{ color: 'green' }}>check_circle</Icon>
    </Tooltip>
  ) : status === MilestoneState.STARTED ? (
    <Tooltip title={<LanguageString groupName="ROADMAP" resourceName="MILESTONE_STARTED" defaultText="Started" />}>
      <Icon style={{ color: '#f1c40f' }}>swap_vertical_circle</Icon>
    </Tooltip>
  ) : status === MilestoneState.NOT_STARTED ? (
    <Tooltip title={<LanguageString groupName="ROADMAP" resourceName="MILESTONE_NOT_STARTED" defaultText="No Started" />}>
      <Icon color="error">error</Icon>
    </Tooltip>
  ) : (
    <Tooltip title="Not in path">
      <Icon style={{ color: 'black' }}>clear</Icon>
    </Tooltip>
  );

const TsIcon: React.FC<{ userTask: UserMilestone['tasks'][number] }> = ({ userTask }) => {
  const task = userTask.task;
  const assignment = userTask.assignment;
  const status = taskStatus(assignment);
  const style = { width: '24px', heigth: '24px', display: 'flex', justifyContent: 'center' };

  const icon = task.actionType === TaskActionType.webForm
    ? 'list_alt'
    : task.actionType === TaskActionType.video
      ? 'ondemand_video'
      : task.actionType === TaskActionType.link
        ? 'language'
        : task.actionType === TaskActionType.document
          ? 'backup'
          : task.actionType === TaskActionType.readOnly
            ? 'book'
            : 'event';

  return <Tooltip title={<LanguageString languageString={task.name} />}>{
    status === MilestoneState.COMPLETE
      ? <Icon style={{ color: 'green', ...style }}>{icon}</Icon>
      : status === MilestoneState.STARTED
        ? <Icon style={{ color: '#f1c40f', ...style }}>{icon}</Icon>
        : <Icon style={{ color: 'red', ...style }}>{icon}</Icon>}
  </Tooltip>;
};

const msName = (ms: Milestone) => {
  return ms.tasks?.[0]?.name;
};

export const MilestoneList: React.FC<MilestoneListProps> = props => {
  const { taskDetail, userMilestones, buttons } = props;
  const classes = useStyles();
  const childLinks = useChildLinks({
    items: userMilestones ? userMilestones.map(um => ({id: um.milestone.id, ...um})) : [],
    paramMap: { id: 'msId' },
    routeName: 'milestone'
  });

  const maxTasks = taskDetail ? Math.max(...userMilestones.map(um => um.tasks.length)) : 1;

  return <>{childLinks
    ? <List className={classes.container}>
      {childLinks.map(cl => cl.item.rmMilestone == null
        ? <ListItem>No milestone found</ListItem>
        : <ListItem key={cl.item.milestone.id} component={RouterLink} to={`${cl.path}${cl.item.rmMilestone.id ? `/${cl.item.rmMilestone.id}` : ''}`} button>
          <ListItemIcon>
            <MsIcon status={milestoneStatus(cl.item)} />
          </ListItemIcon>
          {taskDetail && <div style={{ display: 'flex', flexDirection: 'row', width: `${26 * maxTasks}px`, justifyContent: 'start', marginRight: '8px', marginLeft: '-12px' }}>
            {cl.item.tasks?.map(t => <TsIcon userTask={t} />)}
          </div>}
          <ListItemText id={String(cl.item.id)}>
            {(() => {
              const lsName = msName(cl.item.milestone);
              const rms = cl.item.rmMilestone.id != null && cl.item.milestone.isUserNameable ? cl.item.rmMilestone : undefined;

              return rms != null ? <Goal goalItem={cl.item.rmMilestone} gp="customName" tp="translation" lsData={lsName} placeholder="MS_CUSTOM_NAME" /> :
                <LanguageString useLabel={true} languageString={msName(cl.item.milestone)} />;
            })()}
          </ListItemText>
          {buttons != null ? <ListItemSecondaryAction>{buttons(cl.item.rmMilestone)}</ListItemSecondaryAction> : null}
        </ListItem>)}
    </List>
    : <ChildRoutes />}</>;
};
